import { useState, useRef, useEffect } from "react";
import usePersonaStore from "../../Store/PersonaStore";
import { getPersonaInitial } from "../../Utils/personaUtils.js";
import "../Styles/tooltip.scss";
import Tooltip from "../ui/tooltip.jsx";
import DeletePersonaModal from "./DeletePersonaModal";
import Button from "../ui/Button/Button.jsx";
import { ScaleOnHover } from "../ui/Motion";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function PersonaAvatar({ persona, onClick, isSelected }) {
  const { selectedPersona, deletePersona } = usePersonaStore();
  const { t } = useTranslation();
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const contextMenuRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const touchStartPosRef = useRef({ x: 0, y: 0 });

  // Check if this persona is selected
  const isCurrentlySelected =
    isSelected || (selectedPersona && selectedPersona._id === persona._id);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setShowContextMenu(false);
      }
    };

    if (showContextMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showContextMenu]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

    longPressTimerRef.current = setTimeout(() => {
      setContextMenuPosition({ x: touch.clientX, y: touch.clientY });
      setShowContextMenu(true);

      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y);

    if (deltaX > 10 || deltaY > 10) {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowContextMenu(false);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    const result = await deletePersona(persona._id);
    setShowDeleteModal(false);

    if (!result.success) {
      alert(result.message || "Failed to delete persona");
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <Tooltip
        content={persona.name}
        placement="top"
        animation="shift-away"
        delay={[400, 0]}
        theme="red-tooltip"
      >
        <ScaleOnHover
          className="flex justify-center cursor-pointer p-[1px]"
          onClick={onClick}
          onContextMenu={handleContextMenu}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className={`w-11 h-11 rounded-full flex items-center justify-center bg-neutral-100 overflow-hidden ${
              isCurrentlySelected
                ? "border-2 border-red-500"
                : "border border-gray-300 opacity-50"
            }`}
          >
            {persona.image ? (
              <img
                src={persona.image}
                alt={persona.name}
                className={`w-full h-full object-cover ${
                  isCurrentlySelected ? "" : "grayscale"
                }`}
              />
            ) : (
              <span
                className={`text-xl font-bold ${
                  isCurrentlySelected ? "text-red-500" : "text-gray-500"
                }`}
              >
                {getPersonaInitial(persona.name)}
              </span>
            )}
          </div>
        </ScaleOnHover>
      </Tooltip>

      {/* Context Menu */}
      {showContextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-[9999] bg-white border border-neutral-green-200 rounded-lg shadow-lg min-w-[120px]"
          style={{
            left: `${contextMenuPosition.x}px`,
            top: `${contextMenuPosition.y}px`,
          }}
        >
          <Button
            variant="nav"
            size="sm"
            fullWidth
            onClick={handleDeleteClick}
            // className="text-red-600 hover:bg-red-50 justify-start"
            leftIcon={<Trash2 className="w-4 h-4" />}
          >
            {t("common.delete")}
          </Button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeletePersonaModal
        isOpen={showDeleteModal}
        personaName={persona.name}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
}
